import { DeleteButton } from "@components/buttons/delete";
import { PlusButton } from "@components/buttons/plus";
import { RollBackButton } from "@components/buttons/rollback";
import {
  SHBox,
  SHChip,
  SHImage,
  SHStack,
  SHStackProps,
  SHTypography,
} from "@components/design-systems";
import {
  CheckCircleSVG,
  CrossCircleSVG,
  EditSVG,
  PlusSVG,
} from "@components/svgs";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { SelectedProductDTO } from "@models/platform-analysis/entities/steps/feature";
import {
  AnalysisDecisionStatus,
  DecisionBannerIconType,
} from "@models/platform-analysis/enums/feature/decisionStatus";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { useTheme } from "@mui/material";
import { AnalysisDecisionCommentDialog } from "@pages/platform-analysis-v2/_id/components/dialogs/product-comment";
import {
  DECISION_BANNER_CONFIG,
  DECISION_CONTAINER_BACKGROUND_CONFIG,
} from "@pages/platform-analysis-v2/_id/steps/fee/analysis/constants";
import { isEmpty, isEqual } from "lodash";
import { useMemo } from "react";
import { useToggle } from "react-use";

export interface FeeProductCardProps extends SHStackProps {
  analysisId: string;
  product: SelectedProductDTO;
  isAdviserView?: boolean;
  isShowButtons?: boolean;
  onModifyComment?: (platformId: string, comment?: string) => void;
  onChangeDecisionStatus?: (
    platformId: string,
    status: AnalysisDecisionStatus,
  ) => void;
}
export const FeeProductCard = ({
  analysisId,
  product,
  isAdviserView,
  ...others
}: FeeProductCardProps) => {
  // #region Hooks
  const { palette } = useTheme();
  const { updateDecision, isReadOnly } = usePlatformAnalysis();

  // #endregion Hooks

  // #region State & Refs
  const { decisionStatus, comment, isProductMain } = product || {};
  const [isSubmitting, toggleIsSubmitting] = useToggle(false);
  const [isShortlisting, toggleIsShortlisting] = useToggle(false);
  const [openCommentDialog, toggleOpenCommentDialog] = useToggle(false);

  const shouldDisableActions = useMemo(() => {
    return isReadOnly || isSubmitting || isShortlisting;
  }, [isReadOnly, isSubmitting, isShortlisting]);

  const color = useMemo(() => {
    return isAdviserView ? palette.common.black : palette.common.white;
  }, [isAdviserView, palette]);

  const showShortListButton = useMemo(() => {
    if (!isAdviserView) return false;

    return (
      isEqual(product.decisionStatus, null) ||
      isEqual(product.decisionStatus, AnalysisDecisionStatus.Undecided)
    );
  }, [isAdviserView, product]);

  const showCommentButton = useMemo(() => {
    if (!isAdviserView) return true;
    
    return (
      isEqual(product.decisionStatus, AnalysisDecisionStatus.Shortlisted) ||
      isEqual(product.decisionStatus, AnalysisDecisionStatus.Disqualified)
    );
  }, [isAdviserView, product]);

  const showDeletedButton = useMemo(() => {
    return (
      isAdviserView &&
      !isEqual(product.decisionStatus, AnalysisDecisionStatus.Disqualified)
    );
  }, [isAdviserView, product]);

  const showRollbackButton = useMemo(() => {
    return isAdviserView && !showDeletedButton;
  }, [isAdviserView, showDeletedButton]);
  // #endregion State & Refs

  // #region Methods
  const renderMainProductBadge = () => {
    if (!isProductMain) return null;
    
    return (
      <SHChip
        label={isAdviserView ? "Current" : "Main"}
        color={"warning"}
        variant={"outlined"}
        sx={{
          position: "absolute",
          top: "-10px",
          right: "-10px",
          fontSize: "10px",
          fontWeight: 500,
          padding: "2px",
          "&>.MuiChip-label": {
            padding: "0px 4px",
            textOverflow: "initial",
          },
          height: "auto",
          maxWidth: "none",
        }}
      />
    );
  };

  const renderIcon = (type?: DecisionBannerIconType) => {
    switch (type) {
      case DecisionBannerIconType.Cross:
        return <CrossCircleSVG />;
      case DecisionBannerIconType.Check:
        return <CheckCircleSVG />;
      default:
        return null;
    }
  };

  const renderDecisionBanner = (
    decisionStatus?: AnalysisDecisionStatus | null,
  ) => {
    if (
      !isAdviserView ||
      !decisionStatus ||
      isEqual(decisionStatus, AnalysisDecisionStatus.Undecided)
    )
      return null;

    const config = DECISION_BANNER_CONFIG[decisionStatus];
    if (!config) return null;
    const { backgroundColor, label, iconType } = config;

    return (
      <SHStack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        height="25px"
        sx={{ backgroundColor, transition: "all 0.5s ease" }}
      >
        {renderIcon(iconType)}
        <SHTypography
          variant="body3"
          colorVariant="white"
          paddingBottom="-2px"
          fontSize="12px"
        >
          {label}
        </SHTypography>
      </SHStack>
    );
  };

  const setDecisionAndComment = async ({
    status = decisionStatus || null,
    comment = null,
    restore = false,
  }: {
    status?: AnalysisDecisionStatus | null;
    comment?: string | null;
    restore?: boolean;
  }) => {
    return await updateDecision({
      analysisId: analysisId,
      analysisStep: AnalysisStep.Fee,
      productId: product.id,
      decisionStatus: status,
      comment: restore ? null : comment,
    });
  };

  const handleOnDisqualify = () => {
    toggleIsSubmitting(true);
    setTimeout(async () => {
      await setDecisionAndComment({
        status: AnalysisDecisionStatus.Disqualified,
      });
      toggleIsSubmitting(false);
    }, 0);
  };

  const handleRestore = () => {
    toggleIsSubmitting(true);
    setTimeout(async () => {
      await setDecisionAndComment({
        status: AnalysisDecisionStatus.Undecided,
        restore: true,
      });
      toggleIsSubmitting(false);
    }, 0);
  };

  const handleShortlist = () => {
    toggleIsShortlisting(true);
    setTimeout(async () => {
      await setDecisionAndComment({
        status: AnalysisDecisionStatus.Shortlisted,
      });
      toggleIsShortlisting(false);
    }, 0);
  };

  const handleOnSubmitComment = (comment: string) => {
    toggleIsSubmitting(true);
    setTimeout(async () => {
      const response = await setDecisionAndComment({
        status: decisionStatus || null,
        comment,
      });
      toggleIsSubmitting(false);

      if (response) toggleOpenCommentDialog(false);
    }, 0);
  };
  // #endregion

  // #region Render

  return (
    <SHStack>
      <SHStack
        {...others}
        width={"100%"}
        spacing={"10px"}
        border={`1px solid ${palette.secondary[100]}`}
        padding={"15px"}
        bgcolor={
          isAdviserView
            ? DECISION_CONTAINER_BACKGROUND_CONFIG[
                decisionStatus || AnalysisDecisionStatus.Undecided
              ]
            : palette.common.white
        }
      >
        <SHStack direction={"row"} alignItems={"center"} spacing={"10px"}>
          <SHBox
            sx={{
              width: "33px",
              height: "33px",
              position: "relative",
            }}
          >
            <SHImage src={product.productLogo?.url} width={33} height={33} />
            {renderMainProductBadge()}
          </SHBox>

          <SHTypography
            variant="subtitle2"
            lineHeight={"120%"}
            sx={{
              wordBreak: "break-word",
            }}
          >
            {product.productName}
          </SHTypography>
        </SHStack>
        <SHStack direction={"row"} alignItems={"center"} spacing={"5px"}>
          {showCommentButton && (
            <SHStack direction={"row"} alignItems={"center"} spacing={"5px"}>
              <PlusButton
                sx={{ width: "120px" }}
                size="medium"
                variant={isAdviserView ? "outlined" : "contained"}
                disabled={shouldDisableActions}
                startIcon={
                  isEmpty(comment?.comment) ? (
                    <PlusSVG color={color} />
                  ) : (
                    <EditSVG color={color} />
                  )
                }
                onClick={toggleOpenCommentDialog}
              >
                Comment
              </PlusButton>
            </SHStack>
          )}
          {showShortListButton && (
            <SHStack direction={"row"} alignItems={"center"} spacing={"5px"}>
              <PlusButton
                sx={{ width: "120px" }}
                size="medium"
                variant={"outlined"}
                disabled={shouldDisableActions}
                isLoading={isShortlisting}
                startIcon={<PlusSVG color={color} />}
                onClick={handleShortlist}
              >
                Shortlist
              </PlusButton>
            </SHStack>
          )}
          {showDeletedButton && (
            <DeleteButton
              height={32}
              width={32}
              backgroundColor={"transparent"}
              borderColor={palette.common.black}
              disabled={shouldDisableActions}
              isLoading={isSubmitting}
              onClick={handleOnDisqualify}
            />
          )}
          {showRollbackButton && (
            <RollBackButton
              height={"32px"}
              width={"32px"}
              backgroundColor={"transparent"}
              borderColor={palette.common.black}
              disabled={shouldDisableActions}
              isLoading={isSubmitting}
              onClick={handleRestore}
            />
          )}
        </SHStack>
      </SHStack>

      {renderDecisionBanner(product.decisionStatus)}

      <AnalysisDecisionCommentDialog
        open={openCommentDialog}
        originalComment={comment?.comment}
        onClose={toggleOpenCommentDialog}
        onSubmit={handleOnSubmitComment}
      />
    </SHStack>
  );
};
